/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiResponse } from "../../types/apiTypes";
import { SearchCompanyResult, Company } from "@/types/companyTypes";
import { baseQueryWithAutoRefresh } from "../BaseQuery";
import { setData, initData, appendData, setIsFetching, dataType, clearData, setQuerySuccess } from "../reducers/searchSlice";
import { TopCompany } from "@/dev/ui/top-company-view";
import { UnifiedCompletionItem } from "@/types/searchTypes";
import type {  BulkOperationResult, ResolvedName } from "@/types/models";

type SearchResult = {
  source: SearchCompanyResult[];
  totalHits: number | undefined;
  page: number;
  limit: number;
};

export const companyApi = createApi({
  reducerPath: "companyApi",
  baseQuery: baseQueryWithAutoRefresh,
  tagTypes: ["Company", "Area"],
  endpoints: (builder) => ({
    postNewCompany: builder.mutation<ApiResponse<{ company: any }>, any>({
      query: (company) => ({
        url: "/admin/doc",
        method: "POST",
        body: {
          companies: [company],
        },
      }),
      invalidatesTags: ["Company"],
    }),
    searchCompanies: builder.mutation<
      ApiResponse<SearchResult>,
      {
        searchTerm: string;
        page?: number;
        published?: boolean;
        area_of_interest?: string[] | undefined;
        total_donation?: number | undefined;
        type?: dataType | "all";
      }
    >({
      query: ({ page, published, total_donation, type, searchTerm = "", area_of_interest }) => {
        return {
          url: "/company/search",
          method: "POST",
          body: {
            query: searchTerm,
            filters: {
              area_of_interest,
              total_donation,
            },
            type: type === 'all'? undefined : type,
            page,
            published,
          },
        };
      },
      onCacheEntryAdded(_, { dispatch }) {
        dispatch(setIsFetching(true));
      },

      async onQueryStarted({ searchTerm, total_donation, area_of_interest, type }, { dispatch, queryFulfilled }) {
        // 1. as soon as the mutation starts, put the term in your slice
        try {
          // 2. wait for the response
          const { data: response } = await queryFulfilled;
   
          // 3. save the returned array into your slice
          const resData = response.data;


          dispatch(
            initData({
              data: resData.source,
              totalHits: resData.totalHits || 0,
              page: resData.page,
              limit: resData.limit,
              searchTerm,
              filters: {
                area_of_interest,
                total_donation,
              },
              type: type ? type : "Company",
            })
          );

          dispatch(setQuerySuccess(true));
        } catch {
          // optional: clear out stale results on error
          dispatch(setQuerySuccess(false));
          dispatch(clearData());
        } finally {
          dispatch(setIsFetching(false));
        }
      },
    }),
    searchUnified: builder.mutation<
      ApiResponse<{ source: any[]; totalHits: number; page: number; limit: number }>,
      {
        query?: string;
        page?: number;
        limit?: number;
        pageSize?: number;
        kind: "all" | "public" | "private" | "nasdaq" | "vip" | "hr" | "marketing";
        kind_filter?: string[];
        filters?: { area_of_interest?: string[]; total_donation?: number; submission_date?: string[]; essence?: string[] };
        _append?: boolean;
        favorites_only?: boolean;
        fav_companies?: string[];
        fav_vips?: string[];
        fav_hr?: string[];
        sort_by?: "relevance" | "donation_asc" | "donation_desc" | "name_asc" | "name_desc" | "market_value_asc" | "market_value_desc" | "surname_asc" | "surname_desc" | "role_asc" | "role_desc" | "full_name_asc" | "full_name_desc" | "company_name_asc" | "company_name_desc" | "company_employee_count_asc" | "company_employee_count_desc";
      }
    >({
      query: ({ query = "", page = 1, limit, pageSize, kind, kind_filter, filters, favorites_only, fav_companies, fav_vips, fav_hr, sort_by }) => {
        const hasFilters = filters && (
          (filters.area_of_interest?.length ?? 0) > 0 ||
          filters.total_donation != null ||
          (filters.submission_date?.length ?? 0) > 0 ||
          (filters.essence?.length ?? 0) > 0
        );
        return {
          url: "/company/unified-search",
          method: "POST",
          body: {
            query,
            page,
            limit: limit ?? pageSize,
            kind,
            kind_filter: kind_filter?.length ? kind_filter : undefined,
            filters: hasFilters ? filters : undefined,
            favorites_only: favorites_only || undefined,
            fav_companies: favorites_only && fav_companies?.length ? fav_companies : undefined,
            fav_vips: favorites_only && fav_vips?.length ? fav_vips : undefined,
            fav_hr: favorites_only && fav_hr?.length ? fav_hr : undefined,
            sort_by: sort_by && sort_by !== "relevance" ? sort_by : undefined,
          },
        };
      },
      onCacheEntryAdded(_, { dispatch }) {
        dispatch(setIsFetching(true));
      },
      async onQueryStarted({ query: q, filters, _append }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const resData = response.data;
          const sliceType: dataType = "unified";
          if (_append) {
            dispatch(appendData(resData));
          } else {
            dispatch(
              initData({
                data: resData.source,
                totalHits: resData.totalHits || 0,
                page: resData.page,
                limit: resData.limit,
                searchTerm: q ?? "",
                filters: filters ?? {},
                type: sliceType,
              })
            );
          }
          dispatch(setQuerySuccess(true));
        } catch {
          dispatch(setQuerySuccess(false));
          if (!_append) dispatch(clearData());
        } finally {
          dispatch(setIsFetching(false));
        }
      },
    }),
    getUnifiedCompletion: builder.query<ApiResponse<UnifiedCompletionItem[]>, { query: string; max?: number; kind?: "vip" | "hr" | "marketing" }>({
      query: ({ query, max, kind }) => ({
        url: `/company/unified-completion?query=${encodeURIComponent(query)}${max != null ? `&max=${max}` : ""}${kind ? `&kind=${kind}` : ""}`,
        method: "GET",
      }),
    }),
    getNextPageAllCompanies: builder.mutation<ApiResponse<SearchResult>, { page: number }>({
      query: ({ page }) => ({
        url: "/company/search",
        method: "POST",
        body: {
          page,
          published: false,
        },
      }),
    }),
    getNextCompanyPage: builder.mutation<
      ApiResponse<SearchResult>,
      {
        searchTerm: string;
        page?: number;
        published?: boolean;
        area_of_interest?: string[];
        total_donation?: number;
        type?: string;
      }
    >({
      query: ({ page, searchTerm, published, area_of_interest, total_donation, type }) => {

        return {
          url: "/company/search",
          method: "POST",
          body: {
            query: searchTerm,
            filters: {
              area_of_interest: area_of_interest,
              total_donation: total_donation,
            },
            page: page,
            published,
            type,
          },
        };
      },
    }),
    getCompanyById: builder.query<ApiResponse<Company>, string>({
      providesTags: ["Company"],
      query: (id) => ({
        url: `/company/${id}`,
        method: "GET",
      }),
    }),
    getUnpublishedCompanyById: builder.mutation<ApiResponse<Company>, string>({
      query: (id) => ({
        url: `/admin/doc/${id}`,
        method: "POST",
        body: {
          published: false,
        },
      }),
    }),
    getAllCompanies: builder.query<ApiResponse<SearchResult>, void>({
      query: () => ({
        url: "/company/search",
        method: "POST",
        body: {
          published: false,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // 1. as soon as the mutation starts, put the term in your slice

        try {
          // 2. wait for the response
          const { data: response } = await queryFulfilled;
          // 3. save the returned array into your slice
          const resData = response.data;

          dispatch(
            initData({
              data: resData.source,
              totalHits: resData.totalHits || 0,
              page: resData.page,
              limit: resData.limit,
              searchTerm: "",
              type: "Company",
            })
          );
        } catch {
          // optional: clear out stale results on error
          dispatch(setData([]));
        }
      },
      providesTags: ["Company"],
    }),
    deleteCompany: builder.mutation<ApiResponse<{ company: any }>, string>({
      query: (id) => ({
        url: `/admin/doc/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Company"],
    }),
    updateCompany: builder.mutation<ApiResponse<{ company: any }>, { id: string; updates: Partial<Company> }>({
      query: ({ id, updates }) => ({
        url: `/admin/doc/${id}`,
        method: "PUT",
        body: {
          updates,
        },
      }),
      invalidatesTags: ["Company"],
    }),
    getAreas: builder.query<ApiResponse<{ name: string }[]>, void>({
      query: () => ({
        url: "/company/area_of_interests",
        method: "GET",
      }),
      providesTags: ["Area"],
    }),
    getAutoComplete: builder.query<ApiResponse<string[]>, { query: string; company_type: string }>({
      query: ({ query, company_type }) => ({
        url: `/company/completion?query=${query}&&company_type=${company_type}`,
        method: "GET",
      }),
    }),
    getTop10Companies: builder.query<ApiResponse<TopCompany[]>, void>({
      query: () => ({
        url: "/company/top-companies",
        method: "GET",
      }),
    }),
    bulkOperation: builder.mutation<
      ApiResponse<BulkOperationResult>,
      { url: string; body: Record<string, any> }
    >({
      query: ({ url, body }) => ({
        url,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Company"],
    }),
    resolveNames: builder.mutation<
      ApiResponse<ResolvedName[]>,
      { url: string; names: string[] }
    >({
      query: ({ url, names }) => ({
        url,
        method: "POST",
        body: { names },
      }),
    }),
  }),
});

export const {
  usePostNewCompanyMutation,
  useSearchCompaniesMutation,
  useGetCompanyByIdQuery,
  useGetAllCompaniesQuery,
  useLazyGetAutoCompleteQuery,
  useDeleteCompanyMutation,
  useGetTop10CompaniesQuery,
  useGetNextPageAllCompaniesMutation,
  useUpdateCompanyMutation,
  useGetUnpublishedCompanyByIdMutation,
   useGetNextCompanyPageMutation,
  useGetAreasQuery,
  useSearchUnifiedMutation,
  useLazyGetUnifiedCompletionQuery,
  useBulkOperationMutation,
  useResolveNamesMutation,
} = companyApi;
